import React, { useCallback, useEffect, useMemo } from "react";
import { getCountryOptions } from "../../../pageComponents/reservationPage/utils/shippingCountries";
import {
  DsAutocomplete,
  DsRadioGroup,
  DsTextField,
  FullWidth,
  Title,
} from "../../../common";
import styled from "styled-components";
import { ShippingCountryOptions } from "../../../types/components";
import { getCulture } from "../../../utils/Utils";
import { Controller, useFormContext } from "react-hook-form";
import type { ReservationFormValues } from "../../../pageComponents/reservationPage/reservation-page.schema";
import { useReservationStore } from "../../../stores/useReservationStore";
import { borders, colors, fonts } from "../../common/mixins";
import { DeliveryMethod } from "../../../common/enums/DeliveryMethod";
import { calculateShippingFee } from "../../../pageComponents/reservationPage/utils/calculateShippingFee";
import { getProductTotalQty } from "../../../pageComponents/reservationPage/utils/productQuantities";
import { ShippingLocation } from "../../../common/enums/ShippingLocation";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import { Section } from "../../../pageComponents/reservation-shared-styles";

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ShippingWrap = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 16px;

  ${borders.lightGrayBorder()};
  border-radius: 8px;
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SubTitle = styled.h2`
  ${fonts.normalTextBold()};
  color: ${colors.darkBlue};
  line-height: 16px;
  margin: 0;
`;

type ShippingField = {
  name: keyof ReservationFormValues["shipping"];
  label: string;
  autoComplete: string;
};

function ShippingAddressFields({
  countryOptions,
  r,
  shippingFields,
}: {
  countryOptions: ShippingCountryOptions[];
  r: ReturnType<typeof useReservationResources>;
  shippingFields: ShippingField[];
}) {
  const {
    register,
    control,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext<ReservationFormValues>();

  return (
    <Grid id="shipping-address-section">
      <TextWrap>
        <SubTitle>{r.ShippingAddress}</SubTitle>
      </TextWrap>
      {shippingFields.map(({ name, label, autoComplete }) => (
        <FullWidth key={name}>
          <DsTextField
            label={label}
            autoComplete={autoComplete}
            required
            error={!!errors.shipping?.[name]}
            helperText={errors.shipping?.[name]?.message}
            {...register(`shipping.${name}`, {
              onChange: (e) => {
                if (errors.shipping?.[name]) {
                  if (e.target.value) trigger(`shipping.${name}`);
                  else clearErrors(`shipping.${name}`);
                }
              },
            })}
          />
        </FullWidth>
      ))}
      <FullWidth>
        <Controller
          control={control}
          name="shipping.country"
          render={({ field }) => (
            <DsAutocomplete
              autoSelectSingleOption
              value={
                countryOptions.find((o) => o.value === field.value) ?? null
              }
              options={countryOptions}
              onChange={(_, next: ShippingCountryOptions | null) => {
                field.onChange(next?.value ?? "");
                if (next && errors.shipping?.country) trigger("shipping.country");
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              renderInput={(params) => (
                <DsTextField
                  {...params}
                  label={r.Country}
                  required
                  autoComplete="new-password"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "new-password",
                  }}
                  error={!!errors.shipping?.country}
                  helperText={errors.shipping?.country?.message}
                />
              )}
            />
          )}
        />
      </FullWidth>
    </Grid>
  );
}

/**
 * ShippingAddress
 *
 * Handles delivery method selection (Collect at venue / Ship to address) and
 * the shipping address form. It also calculates and syncs the shipping fee to
 * the form state.
 *
 * PARTIALLY IMPLEMENTED — currently restricted to Greece only:
 * - `ENABLED_SHIPPING_COUNTRIES` limits the country dropdown to ["GR"].
 *   When EU / Worldwide shipping is ready, add the relevant country codes here.
 *
 * - `availableLocations` computes the intersection of shipping locations
 *   supported by ALL shippable products in the cart (Greece=1, Europe=2,
 *   Worldwide=3). This correctly filters out countries a product cannot ship
 *   to, and is already wired up — it just has no effect while only "GR" is
 *   enabled.
 *
 * - `calculateShippingFee` (src/pageComponents/reservationPage/utils/) already
 *   supports all three locations. It will work automatically once more
 *   countries are enabled.
 */

export function ShippingAddress() {
  const r = useReservationResources();
  const SHIPPING_FIELDS: ShippingField[] = [
    { name: "street", label: r.Street, autoComplete: "street-address" },
    { name: "postcode", label: r.Postcode, autoComplete: "postal-code" },
    { name: "number", label: r.StreetNumber, autoComplete: "address-line2" },
    { name: "area", label: r.Region, autoComplete: "address-level1" },
    { name: "city", label: r.City, autoComplete: "address-level2" },
  ];
  const { control, setValue, watch } = useFormContext<ReservationFormValues>();
  const shippingCountry = watch("shipping.country");
  const lang = getCulture()?.split("-")?.[0] ?? "en";
  const countryOptions = useMemo(() => getCountryOptions(lang), [lang]);
  const products = useReservationStore((s) => s.products);
  const deliveryMethodId = watch("deliveryMethodId");

  const productsInCart = useMemo(
    () => products.filter((p) => getProductTotalQty(p) > 0),
    [products],
  );

  const canCollectOnSite =
    productsInCart.length > 0 &&
    productsInCart.every((p) => p.canBeCollectedOnSite);
  const canShip = productsInCart.some((p) => p.canBeShipped);

  const availableLocations = useMemo(
    () =>
      productsInCart
        .filter((p) => p.canBeShipped && p.shippingInfo)
        .reduce<Set<number>>(
          (acc, p) => {
            const productLocations = new Set(
              p.shippingInfo!.map((si) => si.location),
            );
            return new Set(
              Array.from(acc).filter((l) => productLocations.has(l)),
            );
          },
          new Set([
            ShippingLocation.Greece,
            ShippingLocation.Europe,
            ShippingLocation.Worldwide,
          ]),
        ),
    [productsInCart],
  );

  // TODO: expand as more shipping locations are supported
  const ENABLED_SHIPPING_COUNTRIES = new Set(["GR"]);

  const filteredCountryOptions = useMemo(
    () =>
      countryOptions.filter((o) => {
        if (!ENABLED_SHIPPING_COUNTRIES.has(o.value)) return false;
        if (o.isGreece) return availableLocations.has(ShippingLocation.Greece);
        if (o.isEurope) return availableLocations.has(ShippingLocation.Europe);
        return availableLocations.has(ShippingLocation.Worldwide);
      }),
    [countryOptions, availableLocations],
  );

  // Clear country if it's no longer available after cart changes.
  useEffect(() => {
    if (
      shippingCountry &&
      !filteredCountryOptions.some((o) => o.value === shippingCountry)
    ) {
      setValue(
        "shipping.country",
        filteredCountryOptions.some((o) => o.value === "GR") ? "GR" : "",
      );
    }
  }, [filteredCountryOptions, shippingCountry, setValue]);

  const clearFields = useCallback(() => {
    (["street", "postcode", "number", "area", "city"] as const).forEach(
      (field) => setValue(`shipping.${field}`, ""),
    );
    setValue("shipping.country", "GR");
  }, [setValue]);

  // Auto-switch delivery method when cart changes make the current selection unavailable.
  useEffect(() => {
    if (
      !canShip &&
      canCollectOnSite &&
      deliveryMethodId === DeliveryMethod.Ship
    ) {
      setValue("deliveryMethodId", DeliveryMethod.Collect);
    }
    if (
      !canCollectOnSite &&
      canShip &&
      deliveryMethodId === DeliveryMethod.Collect
    ) {
      setValue("deliveryMethodId", DeliveryMethod.Ship);
    }
  }, [canShip, canCollectOnSite, deliveryMethodId, setValue]);

  // Clear shipping fields whenever delivery method is no longer Ship.
  useEffect(() => {
    if (deliveryMethodId && deliveryMethodId !== DeliveryMethod.Ship) {
      clearFields();
    }
  }, [deliveryMethodId, clearFields]);

  // Clear shipping fields when all shippable products are removed from the cart.
  useEffect(() => {
    if (!canShip) {
      clearFields();
    }
  }, [canShip, clearFields]);

  // Keep requiresShipping in sync with the selected delivery method.
  useEffect(() => {
    setValue(
      "requiresShipping",
      canShip && deliveryMethodId === DeliveryMethod.Ship,
      { shouldValidate: true },
    );
  }, [canShip, deliveryMethodId, setValue]);

  // Recalculate shipping fee when delivery method, country, or cart changes.
  useEffect(() => {
    if (deliveryMethodId !== DeliveryMethod.Ship) {
      setValue("shippingFee", 0, { shouldValidate: true });
      return;
    }
    const fee = calculateShippingFee(productsInCart, shippingCountry ?? "");
    setValue("shippingFee", fee, { shouldValidate: true });
  }, [deliveryMethodId, shippingCountry, productsInCart, setValue]);

  if (!canShip && !canCollectOnSite) return null;

  return (
    <>
      {canShip && (
        <Section>
          <Title>{r.DeliveryMethod}</Title>
          {canShip && canCollectOnSite && (
            <>

              <ShippingWrap>
                <Controller
                  control={control}
                  name="deliveryMethodId"
                  render={({ field }) => (
                    <DsRadioGroup
                      name="delivery-type"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      options={[
                        {
                          value: DeliveryMethod.Collect,
                          label: r.CollectAtVenue,
                        },
                        {
                          value: DeliveryMethod.Ship,
                          label: r.SendWithCourier,
                        },
                      ]}
                    />
                  )}
                />

                {deliveryMethodId === DeliveryMethod.Ship && (
                  <ShippingAddressFields
                    countryOptions={filteredCountryOptions}
                    r={r}
                    shippingFields={SHIPPING_FIELDS}
                  />
                )}
              </ShippingWrap>
            </>
          )}

          {canShip &&
            !canCollectOnSite &&
            deliveryMethodId === DeliveryMethod.Ship && (
              <ShippingWrap>
                <ShippingAddressFields
                  countryOptions={filteredCountryOptions}
                  r={r}
                  shippingFields={SHIPPING_FIELDS}
                />
              </ShippingWrap>
            )}
        </Section>
      )}
    </>
  );
}
